import {
    type CompetitorsMediaInvestmentDataKeys,
    modelCompetitorsMediaInvestmentOptions,
} from '@/dashboards/CompetitorsMediaInvestment/api';
import { getCompetitorSpendVariables } from '@/dashboards/CompetitorsMediaInvestment/helpers/getCompetitorSpendVariables';
import { useExecutiveSummaryContext } from '@/dashboards/ExecutiveSummary/context';
import {
    calculateCompetitorSpendTotals,
    type CompetitorSpendTotals,
} from '@/dashboards/ExecutiveSummary/helpers/sumInvestmentInRange';
import { useDashboardGlobalContext } from '@/dashboards/providers/dashboardGlobalContext';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

export type { CompetitorSpendTotals };

export interface CompetitorSpendData extends CompetitorSpendTotals {}

export interface UseCompetitorSpendQueryResult {
    isLoading: boolean;
    isFetching: boolean;
    missingVariables: string[];
    data: CompetitorSpendData;
}

const EMPTY_MISSING_VARIABLES: string[] = [];

/**
 * Competitor Spend — sum of competitor media investment over the selected
 * period.
 *
 * Reuses `modelCompetitorsMediaInvestmentOptions` (the same source the
 * Competitors Media Investment dashboard reads from). That query is not
 * date-parameterised, so a single fetch covers all periods and both the
 * primary and comparison totals are derived from it client-side.
 */
export const useCompetitorSpendQuery = (): UseCompetitorSpendQueryResult => {
    const { primaryRange, comparisonRange } = useExecutiveSummaryContext();
    const {
        dashboard: { dashboardVariables, id: dashboardId },
        isInputDuckDBAvailable,
    } = useDashboardGlobalContext();

    const competitorVariables = useMemo(
        () => getCompetitorSpendVariables(dashboardVariables),
        [dashboardVariables],
    );

    const queryResult = useQuery({
        ...modelCompetitorsMediaInvestmentOptions({ competitorVariables }),
        queryKey: [
            'competitorsMediaInvestment',
            dashboardId,
            competitorVariables,
        ],
        enabled: isInputDuckDBAvailable && competitorVariables.length > 0,
        retry: false,
        gcTime: 0,
    });

    const { rows, fieldKeys, missingVariables } = queryResult.data ?? {};

    const data = useMemo<CompetitorSpendData>(
        () =>
            calculateCompetitorSpendTotals(
                rows,
                fieldKeys,
                primaryRange,
                comparisonRange,
            ),
        [rows, fieldKeys, primaryRange, comparisonRange],
    );

    return {
        isLoading: queryResult.isLoading,
        isFetching: queryResult.isFetching,
        missingVariables: missingVariables ?? EMPTY_MISSING_VARIABLES,
        data,
    };
};
